import { useMutation } from '@tanstack/react-query'
import { createContext, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthApi } from '../services/modules/auth'
import { logout as logoutAction } from '../services/slices/auth'
import { useAppDispatch } from '../services/types/store-types'
import { useRpc } from './RpcContext'
import { useToast } from './toast/ui'

interface AuthContextType {
	isAuthenticated: boolean
	login: (request: TLoginRequest) => void
	logout: () => Promise<void>
	reauth: (request: TReauthRequest) => void
	setUserCaption: (caption: string | null) => void
	isLoading: boolean
	userCaption: string | null
	userRole: TUserRole
}

export interface TLoginRequest {
	user: string
	password: string
}

interface TLoginResponse {
	token: string
}

export type TReauthRequest = {
	token: string
}
export type TReauthResponse = {
	token: string
}

export type TUserRole = 'adm' | 'adm_mc' | 'usr' | ''

const AuthContext = createContext<AuthContextType | undefined>(undefined)

type TParseJWT = {
	user_caption: string
	role: string
}

export const parseJwt = (token: string): TParseJWT => {
	const base64Url = token.split('.')[1]
	const base64 = base64Url.replace('-', '+').replace('_', '/')
	const payload = JSON.parse(atob(base64))

	return {
		user_caption: payload.user_caption,
		role: payload.role,
	}
}

export const AuthProvider = ({ children }: { children: any }) => {
	const [isAuthenticated, setIsAuthenticated] = useState(false)
	const [hasLogout, setHasLogout] = useState(
		!!!localStorage.getItem('authToken')
	)
	const [userCaption, setUserCaption] = useState<string | null>(null)
	const [userRole, setUserRole] = useState<TUserRole>('')
	const { showMessage } = useToast()

	const { callRpcMethod, wsOpened } = useRpc()

	const navigator = useNavigate()
	const dispatch = useAppDispatch()

	const mutationLogin = useMutation<TLoginResponse, Error, TLoginRequest>({
		retry: 1,
		retryDelay: 1000,
		mutationFn: request =>
			callRpcMethod('login', request) as Promise<TLoginResponse>,
		onSuccess: data => {
			console.log(data)
			localStorage.setItem('authToken', data.token)
			const { user_caption, role } = parseJwt(data.token)
			setUserCaption(user_caption)
			setUserRole(role as TUserRole)
			setIsAuthenticated(true)

			navigator('/device', { replace: true })
			//window.open('/device', '_self');
			showMessage({
				message: 'Успешный вход',
				type: 'success',
				caption: 'Успешный вход',
				tag: 'auth',
				timeOut: 3,
			})
		},
		onError: error => {
			showMessage({
				message: String(error),
				type: 'error',
				caption: 'Ошибка авторизации',
				tag: 'auth',
				timeOut: 3,
			})
			setIsAuthenticated(false)
			dispatch(logoutAction())
		},
	})

	const mutationReauth = useMutation<TReauthResponse, Error, TReauthRequest>({
		retry: 3,
		retryDelay: 1000,
		mutationFn: request =>
			callRpcMethod('reauth', request) as Promise<TReauthResponse>,
		onSuccess: (reauthStatus, variables) => {
			setIsAuthenticated(true)
			console.log('reauthStatus', reauthStatus)
			localStorage.setItem('authToken', reauthStatus.token)
			if (!reauthStatus) {
				localStorage.removeItem('authToken')
				dispatch(logoutAction())
				return
			}
			const { user_caption, role } = parseJwt(reauthStatus.token)
			setIsAuthenticated(true)
			setUserCaption(user_caption)
			setUserRole(role as TUserRole)

			showMessage({
				message: 'Успешный вход',
				type: 'success',
				caption: 'Успешный вход',
				tag: 'auth',
				timeOut: 3,
			})
		},
		onError: error => {
			if (error.message === 'socket not ready') {
				showMessage({
					message: 'Сокет еще не готов к работе',
					type: 'error',
					caption: 'Ошибка авторизации',
					tag: 'auth',
					timeOut: 3,
				})
			}
			setIsAuthenticated(false)
			localStorage.removeItem('authToken')
			dispatch(logoutAction())
		},
	})

	const login = (request: TLoginRequest) => {
		const { password, user } = request
		mutationLogin.mutate({ user, password })
	}

	const reauth = (request: TReauthRequest) => {
		mutationReauth.mutate(request)
	}

	const logout = async () => {
		try {
			await AuthApi.logout()
		} catch (e) {
			console.log(e)
		} finally {
			localStorage.removeItem('authToken')
			setIsAuthenticated(false)
			setHasLogout(true)
			dispatch(logoutAction())
		}
	}

	useEffect(() => {
		const token = localStorage.getItem('authToken')
		if (!token) return
		if (hasLogout) return
		if (!isAuthenticated) {
			reauth({ token })
		}
	}, [isAuthenticated, wsOpened])

	useEffect(() => {
		const handleStorage = (event: StorageEvent) => {
			if (event.key === 'authToken') {
				const token = event.newValue
				if (token && !hasLogout) {
					reauth({ token })
				} else if (!token) {
					// Если токен удален из localStorage, выполняем выход
					setIsAuthenticated(false)
					setUserCaption(null)
					setUserRole('')
					dispatch(logoutAction())
				}
			}
		}

		window.addEventListener('storage', handleStorage)

		return () => {
			window.removeEventListener('storage', handleStorage)
		}
	}, []) // Пустой массив зависимостей, обработчик определен один раз
	return (
		<AuthContext.Provider
			value={{
				isAuthenticated,
				login,
				logout,
				reauth,
				setUserCaption,
				isLoading: mutationLogin.isPending,
				userCaption,
				userRole,
			}}
		>
			{children}
		</AuthContext.Provider>
	)
}

export const useAuthContext = () => {
	const context = useContext(AuthContext)
	if (!context) {
		throw new Error('useAuthContext должен использоваться внутри AuthProvider')
	}
	return context
}
